import { useEffect, useMemo, useReducer } from 'react'
import { readServerChartPreference, writeServerChartPreference } from '../lib/chart/chartPreferenceStore.js'

import { readDockLayout } from '../lib/workspace/marketWidgetDock.js'
import { DEFAULT_LAYOUT, normalizeWidgetLayout } from '../lib/workspace/marketWidgetLayout.js'

// User-scoped, asset-independent preference; serialize writes so rapid clicks
// cannot leave an older selection on the server. Never auto-write the default.
export function useMarketWidgetLayout(userId) {
  const [, render] = useReducer(n => n + 1, 0)
  const state = useMemo(() => {
    const defaultValue = DEFAULT_LAYOUT
    const clamp = normalizeWidgetLayout
    const s = { value: defaultValue, status: userId ? 'loading' : 'unavailable', active: false,
      hydrated: false, edited: false, pending: null, saving: false, dirty: false }
    const notify = () => { if (s.active) render() }
    const target = { userId, scope: 'positionWorkspaceChartLab', preferenceKey: 'market-header-layout' }
    s.flush = async () => {
      if (!userId || !s.hydrated || s.saving || s.pending === null) return
      s.saving = true
      s.status = 'saving'
      notify()
      let savedValue
      while (s.pending !== null) {
        const value = s.pending
        s.pending = null
        let ok = false
        try { ok = await writeServerChartPreference({ ...target, preferences: { version: 2, value } }) } catch { /* retry below */ }
        if (ok) savedValue = value
        if (!ok) {
          // A more recent selection takes precedence over the failed request.
          if (s.pending === null) s.pending = value
          s.status = 'error'
          s.saving = false
          notify()
          return
        }
      }
      s.saving = false
      s.dirty = JSON.stringify(s.value) !== JSON.stringify(savedValue)
      s.status = 'saved'
      notify()
    }
    s.load = async () => {
      if (!userId || s.loading) return
      s.loading = true
      s.status = 'loading'
      notify()
      try {
        const stored = await readServerChartPreference({ ...target, throwOnError: true })
        if (!s.edited) s.value = readDockLayout(stored)
        s.hydrated = true
        s.status = 'ready'
        notify()
        void s.flush()
      } catch {
        s.status = 'error'
        notify()
      } finally { s.loading = false }
    }
    s.preview = value => {
      s.value = clamp(value)
      s.edited = true
      s.dirty = true
      notify()
    }
    s.commit = (value = s.value) => {
      s.preview(value)
      s.pending = s.value
      void s.flush()
    }
    s.retry = () => { if (s.hydrated) void s.flush(); else void s.load() }
    return s
  }, [userId])

  useEffect(() => {
    state.active = true
    void state.load()
    const beforeUnload = event => {
      if (userId && (state.dirty || state.saving || state.pending !== null)) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => {
      state.active = false
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [state, userId])

  return { value: state.value, status: state.status, select: state.commit, retry: state.retry }
}
